import type { EffectDefinition, EffectContext } from './types'

interface Firefly {
  x: number
  y: number
  vx: number
  vy: number
  radius: number
  glowRadius: number
  phase: number
  pulseSpeed: number
  targetX: number
  targetY: number
}

function createFirefly(width: number, height: number): Firefly {
  const x = Math.random() * width
  const y = Math.random() * height
  return {
    x,
    y,
    vx: 0,
    vy: 0,
    radius: 1.5 + Math.random() * 1.5,
    glowRadius: 8 + Math.random() * 12,
    phase: Math.random() * Math.PI * 2,
    pulseSpeed: 0.8 + Math.random() * 1.5,
    targetX: x + (Math.random() - 0.5) * 200,
    targetY: y + (Math.random() - 0.5) * 200
  }
}

export const fireflies: EffectDefinition = {
  id: 'fireflies',
  name: 'Fireflies',

  init(ctx: EffectContext) {
    const count = Math.floor((ctx.width * ctx.height) / 20000)
    const items: Firefly[] = []
    for (let i = 0; i < Math.max(15, count); i++) {
      items.push(createFirefly(ctx.width, ctx.height))
    }
    return items
  },

  update(state: unknown, ctx: EffectContext) {
    const items = state as Firefly[]
    const dt = ctx.deltaTime / 1000
    for (const f of items) {
      // Drift toward target
      const dx = f.targetX - f.x
      const dy = f.targetY - f.y
      const dist = Math.sqrt(dx * dx + dy * dy)

      if (dist < 10) {
        f.targetX = f.x + (Math.random() - 0.5) * 300
        f.targetY = f.y + (Math.random() - 0.5) * 300
        f.targetX = Math.max(0, Math.min(ctx.width, f.targetX))
        f.targetY = Math.max(0, Math.min(ctx.height, f.targetY))
      }

      f.vx += (dx / dist) * 20 * dt
      f.vy += (dy / dist) * 20 * dt
      f.vx *= 0.98
      f.vy *= 0.98
      f.x += f.vx * dt
      f.y += f.vy * dt
    }
  },

  draw(state: unknown, ctx: EffectContext) {
    const items = state as Firefly[]
    const { ctx: c, time } = ctx
    const t = time / 1000

    for (const f of items) {
      const pulse = (Math.sin(t * f.pulseSpeed + f.phase) + 1) / 2
      const alpha = 0.15 + pulse * 0.5

      // Glow
      const gradient = c.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.glowRadius * (0.5 + pulse * 0.5))
      gradient.addColorStop(0, `rgba(255, 240, 120, ${alpha * 0.4})`)
      gradient.addColorStop(0.4, `rgba(255, 220, 80, ${alpha * 0.15})`)
      gradient.addColorStop(1, 'transparent')
      c.fillStyle = gradient
      c.fillRect(f.x - f.glowRadius, f.y - f.glowRadius, f.glowRadius * 2, f.glowRadius * 2)

      // Core dot
      c.beginPath()
      c.arc(f.x, f.y, f.radius, 0, Math.PI * 2)
      c.fillStyle = `rgba(255, 250, 160, ${alpha})`
      c.fill()
    }
  }
}
